/**
 * DuplicateCheckModal
 *
 * Surfaces the result of a cross-channel duplicate check and offers
 * the 4 options from the spec:
 *
 *   A. View Existing Lead          — open the existing record (no write)
 *   B. Create New Opportunity      — same customer, new project (writes
 *                                    a new lead row linked to the same
 *                                    customer_id)
 *   C. Request Customer Detail     — submit a change request to a
 *      Update                        manager (phone/email/etc.)
 *   D. Create New Duplicate Lead   — submit force-duplicate request to
 *                                    a manager (rare path)
 *
 * The host modal (e.g. BrokerageLeadModal) is responsible for actually
 * SAVING the new opportunity once the user picks Option B — we hand
 * back a `customer_id` so the host can stamp it on the lead it
 * inserts. This separation keeps the modal reusable across Direct
 * Sales / Indirect Sales / Broker Hub.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertTriangle, Eye, Plus, Edit3, AlertOctagon,
  Loader2, ArrowRight, Handshake, User,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import {
  submitDetailUpdateRequest,
  submitForceDuplicateRequest,
  createCustomer,
  linkLeadToCustomer,
} from '../api/customerMaster';

const SOURCE_TONE = {
  direct:     'bg-sky-50 text-sky-700 border-sky-200',
  indirect:   'bg-amber-50 text-amber-700 border-amber-200',
  broker_hub: 'bg-violet-50 text-violet-700 border-violet-200',
};

const STATUS_TONE = {
  new:               'bg-slate-100 text-slate-700 border-slate-300',
  contacted:         'bg-blue-100 text-blue-700 border-blue-300',
  viewing:           'bg-amber-100 text-amber-700 border-amber-300',
  negotiation:       'bg-orange-100 text-orange-700 border-orange-300',
  booking_initiated: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  won:               'bg-emerald-100 text-emerald-700 border-emerald-300',
  lost:              'bg-red-100 text-red-700 border-red-300',
  cancelled:         'bg-rose-100 text-rose-700 border-rose-300',
};

// MoM-aligned display labels — "contacted" reads as "Prospecting".
const STATUS_LABEL = {
  new: 'New',
  contacted: 'Prospecting',
  viewing: 'Viewing',
  negotiation: 'Negotiation',
  booking_initiated: 'Booking Initiated',
  won: 'Won',
  lost: 'Lost',
  cancelled: 'Cancelled',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

export default function DuplicateCheckModal({
  open,
  onOpenChange,
  // Result from POST /api/customers/check-duplicate
  duplicate,
  // Payload the host was about to submit — we forward it on Option D.
  prospectivePayload,
  // 'broker_sales' or 'contacts' — where Option D would create the new lead.
  prospectiveTarget = 'broker_sales',
  // The broker_id the new lead would be created under. When set, we
  // detect cross-broker conflicts (existing lead under a different
  // broker) and disable "Create New Opportunity" so the user can't
  // accidentally bypass the UAE first-broker-wins rule.
  prospectiveBrokerId = null,
  // Callbacks the host wires up.
  onViewExisting,         // (leadId, source) => void
  onCreateOpportunity,    // (customerId) => Promise<void>  — host saves the lead with this customer_id
  onClose,
}) {
  const { token } = useAuth();
  const [mode, setMode] = useState('options');  // 'options' | 'update' | 'force'
  const [submitting, setSubmitting] = useState(false);
  // Option C — detail update request fields.
  const [updateFields, setUpdateFields] = useState({ phone: '', email: '', nationality: '' });
  const [updateReason, setUpdateReason] = useState('');
  // Option D — force duplicate reason.
  const [forceReason, setForceReason] = useState('');

  // `localMaster` lets us promote a candidate into a master on-demand
  // and update the modal's state in-place — without that, the
  // post-promotion request submissions would still see the original
  // null master from the duplicate-check response. Initialized from
  // the duplicate result and kept in sync via setLocalMaster below.
  const [localMaster, setLocalMaster] = useState(duplicate?.customer || null);
  React.useEffect(() => {
    setLocalMaster(duplicate?.customer || null);
  }, [duplicate]);

  if (!duplicate) return null;

  const master = localMaster;
  const opportunities = duplicate.opportunities || [];
  const candidates = duplicate.candidates || [];

  // We always have something to show — either a master + opportunities
  // (canonical case) or a list of un-mastered candidates (legacy /
  // never-linked rows). The header summary speaks to both.
  const firstLeadDisplay = opportunities[0] || candidates[0];

  // Cross-broker conflict detection. The UAE rule: the FIRST broker
  // to register a client owns commission on that client; another
  // broker creating the same client is the canonical dispute. We
  // flag the conflict when:
  //   - the host told us which broker we're creating under, AND
  //   - any existing opportunity / candidate is under a DIFFERENT broker
  // (existing leads with no broker_id are direct/historical and don't
  // conflict — letting a broker capture a direct customer is fine.)
  const allExisting = [...opportunities, ...candidates];
  const otherBrokerHit = prospectiveBrokerId
    ? allExisting.find(
        (o) => o.broker_id && o.broker_id !== prospectiveBrokerId,
      )
    : null;
  const crossBrokerConflict = Boolean(otherBrokerHit);

  // Ensure we have a Customer Master to act against. Two cases:
  //   - master already exists → return it as-is
  //   - only candidates exist (legacy un-linked rows) → mint a master
  //     from the first candidate, link that candidate row to it, and
  //     return the new master. This is the on-demand backfill that
  //     unblocks Options C and D on legacy data.
  // Idempotent: createCustomer is itself idempotent on mobile/email/
  // emirates_id, so a double-click doesn't create two masters.
  const ensureMaster = async () => {
    if (master) return master;
    const seed = candidates[0];
    if (!seed) {
      throw new Error('No candidate to promote to a Customer Master');
    }
    const { data: newMaster } = await createCustomer(token, {
      name: seed.customer_name || '',
      mobile: seed.mobile || '',
      email: seed.email || '',
      // Best-guess source — match the candidate it was promoted from.
      source: seed.source === 'direct' ? 'direct' :
              seed.source === 'broker_hub' ? 'broker_hub' : 'indirect',
    });
    // Link the legacy lead row to the new master so they show up as
    // related opportunities going forward. The collection name maps
    // 'direct' → contacts, everything else → broker_sales.
    try {
      const coll = seed.source === 'direct' ? 'contacts' : 'broker_sales';
      await linkLeadToCustomer(token, newMaster.id, seed.id, coll);
    } catch (_e) {
      // Non-fatal — the master exists either way; only the back-link
      // failed. Surface a soft warning so the user knows.
      console.warn('[DuplicateCheckModal] candidate-link failed', _e);
    }
    setLocalMaster(newMaster);
    return newMaster;
  };

  const handleCreateOpportunity = async () => {
    setSubmitting(true);
    try {
      // If there's no master yet (i.e. only legacy candidates), we
      // still let the host save its lead — it will create the master
      // inline via the brokerage_hub create_lead auto-resolver and
      // tag both with the same customer_id automatically.
      const cid = master?.id || null;
      await onCreateOpportunity?.(cid);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the opportunity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitUpdate = async () => {
    const changes = [];
    if (updateFields.phone)       changes.push({ field: 'mobile',      new_value: updateFields.phone });
    if (updateFields.email)       changes.push({ field: 'email',       new_value: updateFields.email });
    if (updateFields.nationality) changes.push({ field: 'nationality', new_value: updateFields.nationality });
    if (changes.length === 0) {
      toast.error('Enter at least one new value to request');
      return;
    }
    setSubmitting(true);
    try {
      // Promote candidate → master on-demand if needed. Lets this
      // path work on legacy data where a duplicate was detected
      // against a broker_sales row that was never linked.
      const m = await ensureMaster();
      await submitDetailUpdateRequest(token, {
        customer_id: m.id,
        field_changes: changes,
        reason: updateReason,
      });
      toast.success('Update request submitted for manager approval');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not submit the request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForce = async () => {
    if (!forceReason.trim()) {
      toast.error('Please provide a reason for the duplicate');
      return;
    }
    setSubmitting(true);
    try {
      // Same on-demand promotion as Option C — guarantees the
      // approval audit trail has a customer_id to attach to.
      const m = await ensureMaster();
      await submitForceDuplicateRequest(token, {
        existing_customer_id: m.id,
        existing_lead_id: firstLeadDisplay?.id,
        existing_lead_source: firstLeadDisplay?.source,
        new_lead_target: prospectiveTarget,
        new_lead_payload: prospectivePayload,
        reason: forceReason,
      });
      toast.success('Duplicate request submitted for manager approval');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not submit the request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            Customer Already Exists
          </DialogTitle>
          <DialogDescription>
            A customer matching these contact details was found across our channels.
            Pick how you want to proceed.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Customer summary panel */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <User size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold text-slate-800">
                    {master?.name || firstLeadDisplay?.customer_name || 'Existing customer'}
                  </span>
                  {master && (
                    <Badge variant="outline" className="text-[10px]">
                      CUST {master.id?.slice(0, 8)}
                    </Badge>
                  )}
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  {master?.mobile || firstLeadDisplay?.mobile || '—'}
                  {' · '}
                  {master?.email || firstLeadDisplay?.email || '—'}
                  {master?.nationality && ` · ${master.nationality}`}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  First seen via{' '}
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${SOURCE_TONE[master?.first_source || firstLeadDisplay?.source] || ''}`}
                  >
                    {master?.first_source || firstLeadDisplay?.source_label || '—'}
                  </Badge>
                  {' · '}
                  {fmtDate(master?.created_at || firstLeadDisplay?.created_at)}
                </div>
              </div>
            </div>
          </div>

          {/* Existing opportunities / candidate leads */}
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {opportunities.length > 0 ? 'Linked opportunities' : 'Matching leads'}
              </span>
              <span className="text-[11px] text-slate-500">
                {(opportunities.length || candidates.length)} found
              </span>
            </div>
            <ul className="divide-y divide-slate-100">
              {(opportunities.length > 0 ? opportunities : candidates).slice(0, 5).map((o) => {
                const st = (o.status || 'new').toLowerCase();
                return (
                  <li key={`${o.source}-${o.id}`} className="flex items-center gap-3 px-3 py-2">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${SOURCE_TONE[o.source] || ''}`}
                    >
                      {o.source_label || o.source}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-800">
                        {o.project || o.customer_name || 'Untitled opportunity'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {o.assigned_to ? `Assigned · ${o.assigned_to}` : 'Unassigned'}
                        {o.broker_name ? ` · via ${o.broker_name}` : ''}
                        {' · '}
                        {fmtDate(o.created_at)}
                      </div>
                    </div>
                    <Badge className={`text-[10px] ${STATUS_TONE[st] || STATUS_TONE.new}`}>
                      {STATUS_LABEL[st] || st}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => onViewExisting?.(o.id, o.source)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      title="View this lead"
                    >
                      <Eye size={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Cross-broker conflict banner. The UAE first-broker-wins
              rule means we can't let a second broker silently create
              a parallel lead — that's the commission dispute we're
              trying to prevent. */}
          {mode === 'options' && crossBrokerConflict && (
            <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-rose-800">
                <AlertOctagon size={15} /> Cross-broker conflict
              </div>
              <p className="text-[12px] text-rose-700">
                This client is already registered under{' '}
                <strong>{otherBrokerHit.broker_name || 'another brokerage'}</strong>.
                You cannot create a parallel lead under a different broker — the
                first broker holds commission rights on this client. If a parallel
                record is genuinely needed (e.g. a corporate vs personal enquiry),
                use <strong>Force New Duplicate Lead</strong> below; a manager
                will approve or reject.
              </p>
            </div>
          )}

          {/* ---- Mode-switched action area ---- */}
          {mode === 'options' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <OptionCard
                tone="sky"
                icon={Eye}
                title="View Existing Lead"
                description="Open the existing record to see status, follow-ups, site visits and booking."
                onClick={() => firstLeadDisplay && onViewExisting?.(firstLeadDisplay.id, firstLeadDisplay.source)}
                disabled={!firstLeadDisplay}
              />
              <OptionCard
                tone="emerald"
                icon={Plus}
                title="Create New Opportunity"
                description={
                  crossBrokerConflict
                    ? `Disabled — client belongs to ${otherBrokerHit.broker_name || 'another brokerage'}.`
                    : 'Same customer, new project / unit / budget. Linked under the existing Customer ID.'
                }
                onClick={handleCreateOpportunity}
                loading={submitting}
                primary={!crossBrokerConflict}
                disabled={crossBrokerConflict}
                disabledHint={crossBrokerConflict
                  ? `First broker (${otherBrokerHit.broker_name || 'another firm'}) owns this client`
                  : undefined}
              />
              <OptionCard
                tone="amber"
                icon={Edit3}
                title="Request Detail Update"
                description="Submit a request to update phone, email, or nationality. Needs manager approval."
                onClick={() => setMode('update')}
                // Enabled whenever we have something to anchor the
                // update to — either an existing master, or a
                // candidate row we'll promote into one on submit.
                disabled={!master && candidates.length === 0}
                disabledHint={
                  !master && candidates.length === 0
                    ? 'No customer record to update against'
                    : undefined
                }
              />
              <OptionCard
                tone="rose"
                icon={AlertOctagon}
                title="Force New Duplicate Lead"
                description={
                  crossBrokerConflict
                    ? 'Submit for manager approval. Required when both brokers genuinely need the lead.'
                    : 'Rare. Manager approves a deliberate duplicate. Audit-logged.'
                }
                onClick={() => setMode('force')}
                primary={crossBrokerConflict}
              />
            </div>
          )}

          {mode === 'update' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-800">
                <Edit3 size={14} /> Request customer detail update
              </div>
              <p className="mb-2 text-[11px] text-slate-500">
                Submit new values for the manager to approve. Leave blank for fields you
                don't want to change. The current values are preserved until approval.
              </p>
              {!master && candidates.length > 0 && (
                <p className="mb-2 rounded border border-amber-300 bg-amber-100/60 px-2 py-1.5 text-[11px] text-amber-800">
                  This customer doesn't have a master record yet — submitting will
                  create one from the matched lead and route your update through it.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Field label="New phone">
                  <Input
                    value={updateFields.phone}
                    onChange={(e) => setUpdateFields((f) => ({ ...f, phone: e.target.value }))}
                    placeholder={master?.mobile || '+971…'}
                  />
                </Field>
                <Field label="New email">
                  <Input
                    type="email"
                    value={updateFields.email}
                    onChange={(e) => setUpdateFields((f) => ({ ...f, email: e.target.value }))}
                    placeholder={master?.email || 'client@email.com'}
                  />
                </Field>
                <Field label="New nationality">
                  <Input
                    value={updateFields.nationality}
                    onChange={(e) => setUpdateFields((f) => ({ ...f, nationality: e.target.value }))}
                    placeholder={master?.nationality || 'UAE, Indian, …'}
                  />
                </Field>
              </div>
              <div className="mt-2">
                <Field label="Reason (visible to manager)">
                  <Textarea
                    rows={2}
                    value={updateReason}
                    onChange={(e) => setUpdateReason(e.target.value)}
                    placeholder="Why is this update needed?"
                  />
                </Field>
              </div>
            </div>
          )}

          {mode === 'force' && (
            <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-800">
                <AlertOctagon size={14} /> Force-create a duplicate lead
              </div>
              <p className="mb-2 text-[11px] text-slate-500">
                Use only when the business genuinely needs a parallel record (e.g. a
                corporate enquiry distinct from a personal one). Pending records are
                visible to admins until approved.
              </p>
              <Field label="Reason (required)">
                <Textarea
                  rows={3}
                  value={forceReason}
                  onChange={(e) => setForceReason(e.target.value)}
                  placeholder="Explain why a duplicate record is needed…"
                />
              </Field>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {mode !== 'options' && (
            <Button variant="ghost" onClick={() => setMode('options')} disabled={submitting}>
              ← Back to options
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            {mode === 'update' && (
              <Button onClick={handleSubmitUpdate} disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
                {submitting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Submit update request
              </Button>
            )}
            {mode === 'force' && (
              <Button onClick={handleSubmitForce} disabled={submitting} className="bg-rose-600 hover:bg-rose-700">
                {submitting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Submit duplicate request
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const OptionCard = ({
  icon: Icon, title, description, onClick, tone = 'sky',
  primary = false, loading = false, disabled = false, disabledHint,
}) => {
  const toneStyles = {
    sky:     'border-sky-200 hover:border-sky-400 hover:bg-sky-50',
    emerald: 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50',
    amber:   'border-amber-200 hover:border-amber-400 hover:bg-amber-50',
    rose:    'border-rose-200 hover:border-rose-400 hover:bg-rose-50',
  }[tone];
  const iconBg = {
    sky:     'bg-sky-100 text-sky-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber:   'bg-amber-100 text-amber-700',
    rose:    'bg-rose-100 text-rose-700',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      title={disabled ? disabledHint : undefined}
      className={`group flex flex-col items-start gap-2 rounded-lg border-2 bg-white p-3 text-left transition ${toneStyles} ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${primary ? 'ring-2 ring-violet-200' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${iconBg}`}>
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />}
        </span>
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        {primary && <Badge className="ml-1 bg-violet-600 text-white text-[9px]">Recommended</Badge>}
      </div>
      <p className="text-[11px] text-slate-500">{description}</p>
      <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-400 group-hover:text-slate-600">
        Continue <ArrowRight size={10} />
      </span>
    </button>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
